import {
  BoxSection,
  ButtonDefault,
  ButtonPrimary,
  ConfirmModal,
  Heading3,
  Heading4,
  TextM,
} from 'app/components';
import { Tooltip, useTheme } from '@mui/material';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ReviewStepBox, ReviewStepContent, TedModalContent } from './styles';
import { ApplicationRequestContext } from '../ApplicationRequest';
import { StepContentButtonsArea } from '../styles';
import { ArrowBack, InfoOutlined } from '@mui/icons-material';
import { formatCurrency, normalizeCpfCnpj } from 'app/utils/normalizer';
import { useApplicationsService } from 'modules/products/services/hooks';
import { TBorrowerType } from '../BorrowerStep/BorrowerStep';
import {
  parseBorrowerType,
  parseSubscribers,
  parseConsignedConfig,
  parseBeneficiaryAccount,
} from './utils/borrowerTypeParser';
import { formatPhone } from 'app/helpers';
import { useNavigate } from 'react-router-dom';
import { PixType } from 'modules/customer/context/CustomerProvider/customer.interfaces';
import { useAppContext } from 'app/context/AppContextProvider';
import { format, isSameDay } from 'date-fns';
import useCreditSimulatorV2 from 'app/hooks/useCreditSimulatorV2';
import { SignatureProvider } from '../../interfaces';
import {
  SignatureAuthenticationMode,
  SignatureUnicoAuthenticationMode,
} from 'modules/products/services/api/applicationsHttpService';
import {
  EBeneficiaryType,
  TypeProductEmission,
} from 'modules/products/context/ProductsProvider/products.interfaces';
import { ConsigneeOrganType } from '../ConsignedStep/interfaces';
import { buildGuaranteeInfoPayload } from '../ConsultaFgts/interfaces';
import { ESplitPaymentType } from 'modules/products/interfaces/splitPayment';
import FgtsReviewTable from './FgtsTable/FgtsTable';
import {
  ReviewStepConsignedSiape,
  ReviewStepBorrowerAccount,
  ReviewStepBeneficiaryAccount,
  ReviewStepCustodian,
  ReviewStepSplitBeneficiaries,
  ReviewStepSubscribers,
  ReviewStepPaymentMethod,
  ReviewStepSimulationV2,
  ReviewStepSignature,
  ReviewStepBilletsAccordion,
  ReviewStepQRCodeAccordion,
  ReviewStepInstallmentsAccordion,
} from './components';

enum PaymentMethod {
  PIX = 'PIX',
  TED = 'TED',
}

enum TedErrorType {
  BORROWER,
  HOUR,
}

const signatureOptions = [
  {
    value: SignatureAuthenticationMode.HANDWRITTEN,
    label: 'Assinatura manuscrita',
  },
  {
    value: SignatureAuthenticationMode.SELFIE,
    label: 'Selfie com documento',
  },
  {
    value: SignatureAuthenticationMode.ADDRESS_PROOF,
    label: 'Comprovante de endereço',
  },
  {
    value: SignatureAuthenticationMode.OFFICIAL_DOCUMENT,
    label: 'Documento oficial',
  },
  {
    value: SignatureAuthenticationMode.LIVENESS,
    label: 'Selfie dinâmica',
  },
  {
    value: SignatureAuthenticationMode.PIX,
    label: 'Pix',
  },
  {
    value: SignatureAuthenticationMode.FACIAL_BIOMETRICS,
    label: 'Biometria Facial',
  },
  {
    value: SignatureAuthenticationMode.ICP_BRASIL,
    label: 'Certificado Digital',
  },
  {
    value: SignatureAuthenticationMode.EMAIL,
    label: 'E-mail',
  },
  {
    value: SignatureAuthenticationMode.SMS,
    label: 'SMS',
  },
  {
    value: SignatureAuthenticationMode.WHATSAPP,
    label: 'WhatsApp',
  },
];

const signatureUnicoOptions = [
  {
    value: SignatureUnicoAuthenticationMode.DOC_SIGN_SERPRO,
    label: 'Serpro',
  },
  {
    value: SignatureUnicoAuthenticationMode.DOC_SIGN,
    label: 'Padrão',
  },
];

const ReviewStep = () => {
  /* Hooks */
  const {
    simulationV2,
    simulationV2Request,
    product,
    borrowerData,
    subscribers,
    customVariables,
    funding,
    beneficiaryAccount,
    basePremiumAmount,
    custodian,
    billetValidationResponse,
    qrcodeEmvValidationResponse,
    splitPayments,
    updateSimulationV2,
    onBack,
    onBackTwoSteps,
    hasVariablesStep,
    consignedProduct: consigned,
    consignedConfig,
    customCcbTemplateId,
    fgtsPeriods,
    loanGuaranteeInfo,
    loanGuaranteeFormValues,
  } = useContext(ApplicationRequestContext);
  const { feature_flags } = useAppContext();
  const { loadings, createApplication } = useApplicationsService();
  const { simulate: simulateV2 } = useCreditSimulatorV2();
  const theme = useTheme();
  const navigate = useNavigate();

  /* States */
  const [isConfirmModalOpen, toggleConfirmModalOpen] = useState(false);
  const [selectedSignatureType, setSelectedSignatureType] = useState('LINK');
  const [signatureMethod, updateSignatureMethod] =
    useState<SignatureAuthenticationMode>(
      SignatureAuthenticationMode.FACIAL_BIOMETRICS,
    );
  const [signatureMethodUnico, setSignatureMethodUnico] =
    useState<SignatureUnicoAuthenticationMode>(
      SignatureUnicoAuthenticationMode.DOC_SIGN_SERPRO,
    );
  const [selectedSignatureProvider, setSelectedSignatureProvider] = useState<
    string | undefined
  >(undefined);
  const [tedErrorType, setTedErrorType] = useState<TedErrorType | undefined>();
  const [paymentMethod, setSelectedPaymentMethod] = useState<
    undefined | PaymentMethod
  >();
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [isQRCodeAccordionOpen, setIsQRCodeAccordionOpen] = useState(false);
  const [isBilletsAccordionOpen, setIsBilletsAccordionOpen] = useState(false);
  const originalSimulationV2RequestRef =
    useRef<typeof simulationV2Request>(null);

  /* Variables */
  const borrowerType: TBorrowerType = product?.borrower_type as TBorrowerType;
  const tableHeader = [
    'Função',
    'Pessoa',
    borrowerType === 'PERSON' ? 'CPF' : 'CNPJ',
    'Telefone',
    'E-mail',
  ];

  const isFgts = product?.organ_type === ConsigneeOrganType.FGTS;
  const isNewSimulator = useMemo(() => {
    return feature_flags?.NEW_SIMULATOR_ENABLED
      ? feature_flags?.NEW_SIMULATOR_ENABLED?.isActive
      : false;
  }, [feature_flags]);

  const isPaymentMethodVisible = useMemo(() => {
    return feature_flags?.PORTAL_TED_ENABLED
      ? feature_flags?.PORTAL_TED_ENABLED?.isActive
      : false;
  }, [feature_flags]);

  const availableSignatureProviders = useMemo(() => {
    const defaultProviders = [
      { value: SignatureProvider.CLICKSIGN, label: 'Clicksign' },
      { value: SignatureProvider.ZAPSIGN, label: 'ZapSign' },
    ];

    if (
      !product?.allowed_signature_providers ||
      product.allowed_signature_providers.length === 0
    ) {
      return defaultProviders;
    }

    const allProviders = [
      { value: SignatureProvider.CLICKSIGN, label: 'Clicksign' },
      { value: SignatureProvider.ZAPSIGN, label: 'ZapSign' },
      { value: SignatureProvider.CELCOIN, label: 'Celcoin' },
      { value: SignatureProvider.UNICO, label: 'Unico' },
    ];

    return allProviders.filter((provider) =>
      product.allowed_signature_providers?.includes(provider.value),
    );
  }, [product?.allowed_signature_providers]);

  const splitBeneficiaries = useMemo(
    () =>
      splitPayments.filter(
        (payment) => payment.type === ESplitPaymentType.BENEFICIARY,
      ),
    [splitPayments],
  );

  const shouldSkipVariablesStep = useMemo(
    () => !hasVariablesStep,
    [hasVariablesStep],
  );

  useEffect(() => {
    if (availableSignatureProviders.length > 0 && !selectedSignatureProvider) {
      setSelectedSignatureProvider(availableSignatureProviders[0].value);
    }
  }, [availableSignatureProviders, selectedSignatureProvider]);

  const onCreateApplication = useCallback(() => {
    const signatureMethodToUse =
      selectedSignatureProvider === SignatureProvider.UNICO
        ? signatureMethodUnico
        : signatureMethod;

    if (product && selectedSignatureProvider) {
      const guaranteeInfo = buildGuaranteeInfoPayload(
        loanGuaranteeInfo,
        loanGuaranteeFormValues,
      );

      createApplication(
        product.id,
        borrowerData?.id || '',
        funding as string,
        customVariables,
        subscribers,
        selectedSignatureType,
        beneficiaryAccount,
        custodian,
        billetValidationResponse,
        qrcodeEmvValidationResponse,
        paymentMethod,
        simulationV2Request,
        selectedSignatureProvider as SignatureProvider | undefined,
        signatureMethodToUse,
        consigned?.balance_check_id,
        splitPayments,
        simulationV2Request?.financialIndicatorCobransaas,
        simulationV2,
        customCcbTemplateId,
        product.organ_type,
        product.multiple_disbursements_dates,
        guaranteeInfo,
      ).then((application) => {
        if (application !== null) {
          navigate(`/products/application/${application.id}/signatures`);
        }
        toggleConfirmModalOpen(false);
      });
    }
  }, [
    simulationV2Request,
    simulationV2,
    product,
    selectedSignatureType,
    beneficiaryAccount,
    paymentMethod,
    selectedSignatureProvider,
    signatureMethod,
    consigned,
    subscribers,
    signatureMethodUnico,
    customCcbTemplateId,
    borrowerData,
    funding,
    customVariables,
    custodian,
    billetValidationResponse,
    qrcodeEmvValidationResponse,
    splitPayments,
    createApplication,
    navigate,
    loanGuaranteeInfo,
    loanGuaranteeFormValues,
  ]);

  const onChangePaymentMethod = useCallback(
    (method: PaymentMethod) => {
      if (method === PaymentMethod.TED) {
        if (
          !borrowerData?.external_bank_account ||
          !borrowerData?.external_bank_account.bank_account
        ) {
          setTedErrorType(TedErrorType.BORROWER);
          return;
        }

        if (
          !simulationV2Request?.disbursementDate ||
          (isSameDay(
            new Date(),
            new Date(simulationV2Request?.disbursementDate),
          ) &&
            new Date().getHours() >= 16)
        ) {
          setTedErrorType(TedErrorType.HOUR);
          return;
        }
      }
      setSelectedPaymentMethod(method);
    },
    [borrowerData, simulationV2Request],
  );

  const handleSelectSignatureType = (value: string) => {
    setSelectedSignatureType(value);
  };

  const isPerson = () => {
    return borrowerType === 'PERSON';
  };

  const getFormatedPix = (pixKey?: string, pixType?: PixType): string => {
    if (!pixKey || !pixType || pixKey.length === 0 || pixType.length === 0)
      return '-';
    if (pixType === PixType.TAXPAYER_ID) return normalizeCpfCnpj(pixKey);
    if (pixType === PixType.PHONE_NUMBER) return formatPhone(pixKey);
    return pixKey;
  };

  const cessionValue = useMemo(() => {
    if (simulationV2) {
      if (!simulationV2) return 0;
      const tacAmount = product?.tac_not_included_cession_amount
        ? 0
        : simulationV2.tac_amount || 0;
      return (
        (simulationV2.requested_amount ?? 0) +
        tacAmount +
        (simulationV2.finance_fee ?? 0) +
        (simulationV2.iof_amount ?? 0) +
        (basePremiumAmount ?? 0) -
        (simulationV2?.discount_amount ?? 0)
      );
    }
  }, [basePremiumAmount, product, simulationV2]);

  useEffect(() => {
    if (simulationV2Request && !originalSimulationV2RequestRef.current) {
      originalSimulationV2RequestRef.current = { ...simulationV2Request };
    }
  }, [simulationV2Request]);

  const handleDisbursementDateChangeV2 = useCallback(
    async (date: string) => {
      if (!product?.id || !simulationV2Request) {
        return;
      }

      try {
        const formattedDate = format(new Date(date), 'yyyy-MM-dd');

        const requestForSimulation =
          originalSimulationV2RequestRef.current || simulationV2Request;
        const response = await simulateV2(product.id, {
          ...requestForSimulation,
          disbursementDate: formattedDate,
        });

        if (response) {
          const requestToSave =
            originalSimulationV2RequestRef.current || simulationV2Request;

          updateSimulationV2(response, requestToSave, '0');
        }
      } catch (error) {
        console.error('Erro ao simular com data diferente (V2):', error);
      }
    },
    [product?.id, simulationV2Request, simulateV2, updateSimulationV2],
  );
  const successBillets =
    billetValidationResponse?.billets_info?.filter(
      (billet) => billet.succeeded,
    ) ?? [];

  return (
    <BoxSection>
      <ReviewStepContent>
        <Heading3 style={{ marginBottom: '16px' }}>Ficou tudo certo?</Heading3>
        {isFgts && (
          <ReviewStepBox marginTop="0px" marginBottom="16px">
            <Heading4
              style={{
                color: theme.palette.brand.secondary.base,
              }}
            >
              FGTS
            </Heading4>
            <FgtsReviewTable
              data={fgtsPeriods || []}
              simulationResponse={simulationV2}
            />
          </ReviewStepBox>
        )}
        {consigned && product?.organ_type === ConsigneeOrganType.SIAPE && (
          <ReviewStepConsignedSiape
            consignedConfig={parseConsignedConfig(consignedConfig)}
          />
        )}
        {beneficiaryAccount && (
          <ReviewStepBeneficiaryAccount
            beneficiaryAccount={parseBeneficiaryAccount(beneficiaryAccount)}
            getFormatedPix={getFormatedPix}
          />
        )}
        <ReviewStepBorrowerAccount
          borrowerData={borrowerData}
          borrowerType={parseBorrowerType(borrowerType)}
          getFormatedPix={getFormatedPix}
        />
        <ReviewStepSplitBeneficiaries
          beneficiaries={splitBeneficiaries}
          getFormatedPix={getFormatedPix}
        />
        {product?.type_product_emission ===
          TypeProductEmission.COMMERCIAL_PAPER && (
          <ReviewStepCustodian custodian={custodian} />
        )}
        {isNewSimulator && (
          <ReviewStepSimulationV2 simulationV2={simulationV2} />
        )}
        <ReviewStepBox marginTop="16px" marginBottom="16px" padding="30px 40px">
          <Heading4
            style={{
              color: theme.palette.brand.secondary.base,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Tooltip
              placement="bottom-start"
              title={
                <TextM style={{ color: theme.palette.common.white }}>
                  Este valor corresponde ao saldo necessário para liquidação da
                  CCB (Valor Financiado + Ágio).
                </TextM>
              }
            >
              <InfoOutlined
                style={{
                  marginRight: '0.6rem',
                  color: theme.palette.brand.gray[900],
                }}
              />
            </Tooltip>
            <strong>Saldo para liquidação: </strong>
            <span style={{ paddingLeft: '0.4rem' }}>{` ${formatCurrency(
              cessionValue,
              true,
            )}`}</span>
          </Heading4>
        </ReviewStepBox>
        {product?.type_product_emission ===
          TypeProductEmission.BILLET_PAYMENT &&
          successBillets.length > 0 && (
            <ReviewStepBilletsAccordion
              successBillets={successBillets}
              billetValidationResponse={billetValidationResponse}
              isOpen={isBilletsAccordionOpen}
              onClick={() => setIsBilletsAccordionOpen(!isBilletsAccordionOpen)}
            />
          )}
        {product?.type_product_emission === TypeProductEmission.QRCODE_PIX && (
          <ReviewStepQRCodeAccordion
            qrcodeEmvValidationResponse={qrcodeEmvValidationResponse!}
            isOpen={isQRCodeAccordionOpen}
            onClick={() => setIsQRCodeAccordionOpen(!isQRCodeAccordionOpen)}
          />
        )}
        <ReviewStepInstallmentsAccordion
          isOpen={isAccordionOpen}
          onClick={() => setIsAccordionOpen(!isAccordionOpen)}
          product={product}
          simulationV2Request={simulationV2Request}
          simulationV2={simulationV2}
          originalSimulationV2RequestRef={originalSimulationV2RequestRef}
          handleDisbursementDateChangeV2={handleDisbursementDateChangeV2}
        />
        <ReviewStepSubscribers
          tableHeader={tableHeader}
          borrowerData={borrowerData}
          borrowerType={parseBorrowerType(borrowerType)}
          subscribers={parseSubscribers(subscribers)}
          isPerson={isPerson}
        />
        {isPaymentMethodVisible && (
          <ReviewStepPaymentMethod
            paymentMethod={paymentMethod}
            onChangePaymentMethod={onChangePaymentMethod}
          />
        )}
        <ReviewStepSignature
          availableSignatureProviders={availableSignatureProviders}
          selectedSignatureProvider={selectedSignatureProvider}
          setSelectedSignatureProvider={setSelectedSignatureProvider}
          signatureMethod={signatureMethod}
          updateSignatureMethod={updateSignatureMethod}
          signatureMethodUnico={signatureMethodUnico}
          setSignatureMethodUnico={setSignatureMethodUnico}
          selectedSignatureType={selectedSignatureType}
          handleSelectSignatureType={handleSelectSignatureType}
          signatureOptions={signatureOptions}
          signatureUnicoOptions={signatureUnicoOptions}
        />
        <StepContentButtonsArea>
          <ButtonDefault
            margin={true}
            onClick={() =>
              shouldSkipVariablesStep ? onBackTwoSteps() : onBack()
            }
          >
            <ArrowBack />
          </ButtonDefault>
          <ButtonPrimary
            disabled={
              !simulationV2 ||
              !selectedSignatureProvider ||
              (product?.beneficiary_type ===
                EBeneficiaryType.EXTERNAL_REGISTERED_ACCOUNT &&
                !beneficiaryAccount)
            }
            onClick={() => toggleConfirmModalOpen(true)}
          >
            Concluir
          </ButtonPrimary>
        </StepContentButtonsArea>
      </ReviewStepContent>
      <ConfirmModal
        isOpen={isConfirmModalOpen !== false}
        title={
          consigned
            ? 'Concluir solicitação?'
            : 'Concluir solicitação e ir para página de assinaturas?'
        }
        color="primary"
        btnConfirmText="Concluir"
        isLoading={loadings.isApplicationLoading}
        handleConfirm={onCreateApplication}
        handleClose={() => toggleConfirmModalOpen(false)}
        handleCancel={() => toggleConfirmModalOpen(false)}
      >
        {consigned
          ? 'Após concluir a solicitação, a operação será encaminhada para a próxima etapa.'
          : 'Após a assinatura das partes, a solicitação será submetida para aprovação (se necessário).'}
      </ConfirmModal>
      <ConfirmModal
        isColumnButtons={true}
        isOpen={tedErrorType !== undefined}
        title={
          tedErrorType === TedErrorType.BORROWER
            ? 'Tomador não possui conta bancária'
            : 'Fora do Horário'
        }
        btnConfirmText="Entendi"
        handleConfirm={() => setTedErrorType(undefined)}
        handleClose={() => setTedErrorType(undefined)}
      >
        <TedModalContent>
          {tedErrorType === TedErrorType.BORROWER ? (
            <>
              <TextM style={{ lineHeight: '1.3rem' }}>
                O tomador{' '}
                <strong>
                  {borrowerType === 'PERSON'
                    ? borrowerData?.full_name
                    : borrowerData?.legal_name}
                </strong>{' '}
                não possui conta bancária cadastrada para o método de pagamento
                TED.
              </TextM>
              <a
                href={
                  borrowerType === 'PERSON'
                    ? `#/records/natural/${borrowerData?.id}/details`
                    : `#/records/legal/${borrowerData?.id}/details`
                }
                target="_blank"
                style={{
                  textDecoration: 'underline',
                  marginTop: '20px',
                }}
                className="link"
                rel="noreferrer"
              >
                Ir para o cadastro
              </a>
            </>
          ) : (
            <TextM style={{ lineHeight: '1.3rem' }}>
              A <strong>data de liberação do recurso</strong> será hoje e o
              método de pagamento TED só pode ser realizado até às 16h.
            </TextM>
          )}
        </TedModalContent>
      </ConfirmModal>
    </BoxSection>
  );
};

export default ReviewStep;
